import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { clientsApi } from '../api';
import { ArrowLeft, Save, Building2 } from 'lucide-react';

export function ClientNewPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    contact: {
      referent: '',
      email: '',
      phone: '',
      address: '',
    },
    notes: '',
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  const createMutation = useMutation({
    mutationFn: (data: typeof formData) => clientsApi.create(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      navigate(`/clients/${response.data.data.client.id}`);
    },
    onError: (error: any) => {
      // Prova a estrarre errori di validazione dettagliati
      const errorMessage = error?.response?.data?.error?.message || error.message;
      const validationErrors = error?.response?.data?.error?.details;
      
      if (Array.isArray(validationErrors)) {
        // Mappa gli errori di express-validator ai campi del form
        const fieldErrors: Record<string, string> = {};
        validationErrors.forEach((err: any) => {
          const field = err.path || err.param;
          fieldErrors[field] = err.msg || 'Valore non valido';
        });
        setErrors(fieldErrors);
      } else {
        setErrors({ submit: errorMessage });
      }
    },
  });
  
  const validate = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.code.trim()) {
      newErrors.code = 'Il codice è obbligatorio';
    } else if (!/^[A-Z0-9_-]+$/i.test(formData.code)) {
      newErrors.code = 'Solo lettere, numeri, trattini e underscore';
    }
    
    if (!formData.name.trim()) {
      newErrors.name = 'Il nome è obbligatorio';
    }
    
    if (formData.contact.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact.email)) {
      newErrors['contact.email'] = 'Email non valida';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      createMutation.mutate({
        ...formData,
        code: formData.code.toUpperCase(),
      });
    }
  };
  
  const handleChange = (field: string, value: string) => {
    if (field.startsWith('contact.')) {
      const contactField = field.replace('contact.', '');
      setFormData(prev => ({
        ...prev,
        contact: { ...prev.contact, [contactField]: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
    // Clear error on change
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };
  
  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/clients"
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Torna ai clienti
        </Link>
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
            <Building2 className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Nuovo cliente</h1>
            <p className="text-gray-600">Inserisci i dati del nuovo cliente</p>
          </div>
        </div>
      </div>
      
      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {errors.submit && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
            {errors.submit}
          </div>
        )}
        
        {/* Dati principali */}
        <div className="card p-6 space-y-4">
          <h2 className="font-semibold text-gray-900 border-b pb-2">Dati principali</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="code" className="label">
                Codice <span className="text-red-500">*</span>
              </label>
              <input
                id="code"
                type="text"
                value={formData.code}
                onChange={(e) => handleChange('code', e.target.value.toUpperCase())}
                className={`input ${errors.code ? 'border-red-500' : ''}`}
                placeholder="MUSEO-BLU"
              />
              {errors.code && <p className="text-red-500 text-xs mt-1">{errors.code}</p>}
              <p className="text-gray-500 text-xs mt-1">Identificativo univoco (es. MUSEO-BLU)</p>
            </div>
            
            <div>
              <label htmlFor="name" className="label">
                Nome <span className="text-red-500">*</span>
              </label>
              <input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                className={`input ${errors.name ? 'border-red-500' : ''}`}
                placeholder="Museo Blu"
              />
              {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
            </div>
          </div>
          
          <div>
            <label htmlFor="description" className="label">Descrizione</label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              className="input"
              rows={3}
              placeholder="Breve descrizione del cliente..."
            />
          </div>
        </div>
        
        {/* Contatto */}
        <div className="card p-6 space-y-4">
          <h2 className="font-semibold text-gray-900 border-b pb-2">Contatto</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="referent" className="label">Referente</label>
              <input
                id="referent"
                type="text"
                value={formData.contact.referent}
                onChange={(e) => handleChange('contact.referent', e.target.value)}
                className="input"
                placeholder="Mario Rossi"
              />
            </div>
            
            <div>
              <label htmlFor="email" className="label">Email</label>
              <input
                id="email"
                type="email"
                value={formData.contact.email}
                onChange={(e) => handleChange('contact.email', e.target.value)}
                className={`input ${errors['contact.email'] ? 'border-red-500' : ''}`}
                placeholder="info@museo.it"
              />
              {errors['contact.email'] && (
                <p className="text-red-500 text-xs mt-1">{errors['contact.email']}</p>
              )}
            </div>
            
            <div>
              <label htmlFor="phone" className="label">Telefono</label>
              <input
                id="phone"
                type="tel"
                value={formData.contact.phone}
                onChange={(e) => handleChange('contact.phone', e.target.value)}
                className="input"
                placeholder="+39 011 123456"
              />
            </div>
            
            <div>
              <label htmlFor="address" className="label">Indirizzo</label>
              <input
                id="address"
                type="text"
                value={formData.contact.address}
                onChange={(e) => handleChange('contact.address', e.target.value)}
                className="input"
                placeholder="Via Roma 1, Torino"
              />
            </div>
          </div>
        </div>
        
        {/* Note */}
        <div className="card p-6 space-y-4">
          <h2 className="font-semibold text-gray-900 border-b pb-2">Note interne</h2>
          <textarea
            id="notes"
            value={formData.notes}
            onChange={(e) => handleChange('notes', e.target.value)}
            className="input"
            rows={3}
            placeholder="Note visibili solo agli operatori..."
          />
        </div>
        
        {/* Actions */}
        <div className="flex items-center justify-end space-x-3 pt-4">
          <Link to="/clients" className="btn-secondary">
            Annulla
          </Link>
          <button
            type="submit"
            disabled={createMutation.isPending}
            className="btn-primary"
          >
            {createMutation.isPending ? (
              <span className="flex items-center">
                <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                Salvataggio...
              </span>
            ) : (
              <span className="flex items-center">
                <Save className="w-4 h-4 mr-2" />
                Crea cliente
              </span>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
